import { formatEur, type MemberDto } from "@gladis/shared";
import { PersonLabel, SheenSurface } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

import type { CardSummaryDto } from "./ExpenseList.js";

/**
 * A payment source, presented as a physical payment card.
 *
 * ==========================================================================================
 * WHY IT IS A CARD SHAPE AND NOT A TABLE ROW
 * ==========================================================================================
 *
 * A member recognises their own bank card by its shape and its last four digits faster than they read
 * a name — which is the whole job here, since the card is the answer to "whose account did this leave
 * from" and picking the wrong one silently inverts the settle-up. `aspect-[1.586/1]` is the ISO/IEC
 * 7810 ID-1 ratio, the one every card in every wallet has.
 *
 * ==========================================================================================
 * EVERYTHING ON IT IS REAL TEXT (FR-050, FR-053)
 * ==========================================================================================
 *
 * No background-image, no text baked into a graphic, no icon carrying information on its own. The
 * sheen is the only decorative element and it is `aria-hidden` inside `SheenSurface`. So a screen
 * reader reads the name, the identifier, the owner and the monthly total, in that order, and reads
 * nothing about the light.
 *
 * ==========================================================================================
 * THE IDENTIFIER IS ANNOUNCED DIFFERENTLY FROM HOW IT IS SHOWN (FR-056)
 * ==========================================================================================
 *
 * `INGB •••• 4488` is shown; `INGB, ending 4488` is announced. The bullets are U+2022, and screen
 * readers render a run of them as anything from silence to "bullet bullet bullet bullet" — so the
 * visual form goes in an `aria-hidden` span and the spoken form in a visually-hidden one. Two spans
 * rather than an `aria-label` on the visible one, because `aria-label` on a `<span>` is only reliably
 * honoured when the element has a role, and this one has none.
 *
 * ==========================================================================================
 * JOINT AND INDIVIDUAL DIFFER BY MORE THAN COLOUR (FR-057, SC-012)
 * ==========================================================================================
 *
 * Which of the two a card is changes the settle-up entirely — a joint card contributes nothing to it —
 * so the distinction cannot rest on a hue. Three channels, and the first two survive a greyscale
 * screenshot:
 *
 *   1. A TEXT BADGE reading "Joint" or the owner's name. Text, not a dot or an icon.
 *   2. AN EDGE TREATMENT: a doubled left edge on a joint card, a single one on an individual card.
 *      Visible at a glance across a set, and visible with no colour at all.
 *   3. A tint, which varies as well — it may not be the only signal, and here it is the third.
 */

export interface PaymentCardProps {
  card: CardSummaryDto;
  /** The household's members, for the owner's face (020). */
  members?: readonly MemberDto[];
  /**
   * Rendered in the card's action row, and REVEALED ON HOVER — see the note on the footer below.
   *
   * Each must be keyboard-reachable with a visible focus ring, which the reveal is written not to take
   * away: they stay in the DOM and in the tab order at all times.
   */
  actions?: ReactNode;
  /**
   * Stand-in text for the name, shown dimmed while `card.name` is empty.
   *
   * FOR THE LIVE PREVIEW IN `CardForm`, AND FOR NOTHING ELSE. A saved card always has a name — the
   * column is `not null` — so this exists only for the one card that has not been saved yet, where the
   * member is watching it fill in as they type. Without it the heading collapses to nothing and the
   * card opens looking broken rather than blank.
   *
   * The preview reuses THIS component rather than drawing its own rectangle, which is the point: an
   * imitation card would be free to drift from the real one, and the edge treatment, the badge and the
   * masked identifier are the three things a member is being asked to check.
   */
  placeholderName?: string;
}

export function PaymentCard({
  card,
  actions,
  placeholderName,
  members = [],
}: PaymentCardProps): ReactNode {
  const t = useT();
  const isJoint = card.isJoint;
  const hasName = card.name.trim() !== "";

  return (
    <SheenSurface
      radius={260}
      className={[
        // ID-1 proportions. `aspect-[…]` on the surface, so nothing inside can stretch it.
        "aspect-[1.586/1] w-full max-w-[26rem] rounded-2xl border p-5",
        "flex flex-col justify-between",
        // The hover scope the action row reads. See the footer.
        "group",
        /*
         * CHANNEL 2 — the edge. `border-l-8` on a joint card, `border-l-2` on an individual one. A
         * border width is a shape difference, so it survives the greyscale check SC-012 makes, and it
         * is legible from across the page in a way a badge alone is not.
         *
         * THE INSET RING IS GONE, AND THE WIDTH ALONE IS THE CHANNEL.
         *
         * A joint card used to carry `shadow-[inset_0.75rem_0_0_-0.375rem_var(--bg-page)]` as well,
         * to make the thick edge read as a DOUBLED line. Measured against the real stylesheet it put a
         * 6px band of `--bg-page` between the border and the tint — rgb(244,244,251) at x 8–13, with
         * the border ending at x 7 and the tint not starting until x 14. On a card whose whole job is
         * to look like a physical object, a stripe of the page showing through reads as the surface
         * failing to reach its own edge, and it was reported that way twice.
         *
         * Nothing is lost by removing it. 8px against 2px is already a shape difference, which is the
         * property SC-012 needs — the doubling was an embellishment on top of the channel, not the
         * channel. CHANNEL 1's text badge is untouched and remains the one that survives everything.
         */
        isJoint ? "border-l-8 border-line-strong" : "border-l-2 border-line",
        /*
         * CHANNEL 3 — the tint. Present, and deliberately not doing this on its own.
         *
         * `bg-origin-border` IS LOAD-BEARING, AND ITS ABSENCE WAS VISIBLE ON EVERY CARD.
         *
         * A background is PAINTED across the border box by default but POSITIONED against the padding
         * box — `background-clip: border-box` with `background-origin: padding-box`. The two disagree
         * by exactly the border widths, and the default `background-repeat: repeat` fills the
         * difference by WRAPPING the gradient rather than extending it. So the strip under the left
         * border showed the gradient's far end, `to-panel`, which is `transparent` — and both border
         * tokens are translucent, so it read as the card's tint stopping short of its own edge with the
         * page showing through the gap.
         *
         * It is invisible on the 1px sides and obvious on the left, because the left edge is 8px on a
         * joint card and 2px on an individual one — CHANNEL 2 above is what made the defect legible.
         * Positioning against the border box removes the disagreement: one tile, no wrap, the tint
         * starting at the outer edge where the shape starts.
         */
        "bg-origin-border",
        isJoint
          ? "bg-gradient-to-br from-primary/25 to-panel"
          : "bg-gradient-to-br from-secondary to-panel",
        // An archived card is still shown wherever its expenses are (FR-043); it is visibly set aside.
        card.archivedAt === null ? "" : "opacity-70",
      ].join(" ")}
    >
      <header className="relative z-20 flex items-start justify-between gap-3">
        {/*
          `min-w-0` and `break-words`: a long name WRAPS rather than widening the card.

          Without it a card named "Joint household account at the cooperative bank" either overflows the
          rounded rectangle or stretches it out of ID-1 proportions, and the proportions are the reason
          the shape is recognisable at all.
        */}
        <h3
          className={
            "min-w-0 text-base leading-snug font-semibold break-words " +
            (hasName ? "text-ink" : "text-muted")
          }
        >
          {hasName ? card.name : (placeholderName ?? "")}
        </h3>
        {/*
          CHANNEL 1 — the badge, in text.

          "Joint" for a joint card and the owner's name for an individual one, which is the fact a
          member actually needs: not "this is an individual card" but "this is Anouk's card". An
          individual card whose owner deleted their account keeps its snapshotted name (FR-047), and if
          even that is gone it says so rather than showing an empty badge.
        */}
        {/*
          The badge keeps its pill shape and gains a face inside it. `size="sm"` rather than the
          badge's own type scale — the picture is the same 24px it is in every table, so a card and
          a row read as being about the same person rather than as two treatments.
        */}
        <span className="flex shrink-0 items-center gap-1.5 rounded-full border border-line py-0.5 pr-2.5 pl-1 text-xs font-medium tracking-wide whitespace-nowrap text-ink uppercase">
          <PersonLabel
            label={isJoint ? t("person.joint") : (card.ownerName ?? t("person.formerMember"))}
            isPerson={!isJoint && card.ownerName !== null}
            avatarUrl={members.find((m) => m.userId === card.ownerId)?.avatarUrl}
            {...(isJoint
              ? { group: members.map((m) => ({ name: m.displayName, avatarUrl: m.avatarUrl })) }
              : {})}
          />
        </span>
      </header>

      <div className="relative z-20">
        {/* The identifier, shown and announced differently. See the header. */}
        {card.identifier === null ? (
          // A card with no identifier shows no identifier line — not a bare run of bullets (FR-052).
          <p className="text-sm text-muted">{t("exp.card.noIdentifier")}</p>
        ) : (
          <p className="font-mono text-lg tracking-[0.2em] text-ink">
            <span aria-hidden="true">{card.identifier}</span>
            <span className="sr-only">{card.accessibleIdentifier ?? card.identifier}</span>
          </p>
        )}
      </div>

      <footer className="relative z-20 flex items-end justify-between gap-3">
        <div>
          <p className="text-xs tracking-wide text-muted uppercase">{t("exp.list.colPerMonth")}</p>
          {/*
            The card's own monthly total (FR-046) — the sum of the active, started expenses bound to it.
            `tabular-nums` so a set of cards has its digits in a column, which is what makes the set
            comparable at a glance.
          */}
          <p className="text-xl font-semibold tabular-nums text-ink">
            {formatEur(card.monthlyTotalInEuros)}
          </p>
        </div>
        {actions === undefined ? null : (
          /*
           * ==========================================================================================
           * THE ACTIONS ARE REVEALED ON HOVER, AND NOTHING IS HIDDEN FROM ANYONE
           * ==========================================================================================
           *
           * A card is drawn to be recognised as an object at a glance across the set (see the header),
           * and two underlined words in every card's footer are two words in every glance — the set reads
           * as a row of controls with figures attached rather than a wallet. At rest a card now shows only
           * what it IS; the verbs arrive when the pointer does.
           *
           * IT IS `opacity`, NOT `hidden` AND NOT CONDITIONAL RENDERING, and that is the whole of how this
           * keeps FR-055. The buttons stay in the DOM, in document order, in the tab order and in the
           * accessibility tree at every moment — a screen reader reads them from a card nobody is hovering,
           * and the `<ul>` still announces the same four items. Removing them until hover would make every
           * card action unreachable by keyboard and invisible to assistive technology, which is the exact
           * failure this pattern usually ships with.
           *
           * THREE THINGS BRING THEM BACK, and the second and third are not garnish:
           *
           *   `group-hover:` — the pointer, which is what was asked for.
           *   `group-focus-within:` — TABBING. Tailwind v4's `hover` variant is `@media (hover: hover)`, so
           *      without this a keyboard user would move focus onto a fully transparent button and see the
           *      focus ring land on nothing. Focus is a form of "I am here" and gets the same answer.
           *   `[@media(hover:none)]:` — a touch screen, where there is no hover to give and the actions
           *      would otherwise never appear at all. On such a device they are simply always shown.
           *
           * `transition-opacity` rather than a transform, so nothing moves: the total beside it stays
           * exactly where it was, and `motion-reduce` drops even the fade.
           *
           * `data-testid` IS PART OF THE CONTRACT here, for the reason `Page.Body`'s is. Opacity is not an
           * inherited property, so `getComputedStyle` on either button reports `1` whether or not this row
           * is transparent — an E2E assertion pointed at the button would pass in both directions and prove
           * nothing. The row is the element that carries the state, so it has to be the element addressed.
           */
          <div
            data-testid="card-actions"
            className={[
              "flex items-center gap-3 text-sm",
              "opacity-0 transition-opacity duration-150 motion-reduce:transition-none",
              "group-hover:opacity-100 group-focus-within:opacity-100",
              "[@media(hover:none)]:opacity-100",
            ].join(" ")}
          >
            {actions}
          </div>
        )}
      </footer>
    </SheenSurface>
  );
}
